const REMOTE_LOADER_TRUSTED_TYPES_POLICY = "favit-shell-remote-loader";
const REMOTE_LOADER_TRUSTED_TYPES_POLICY_GLOBAL_KEY =
  "__favitRemoteLoaderTrustedTypesPolicy";

type TrustedTypesPolicyLike = {
  createHTML?: (input: string) => unknown;
  createScript?: (input: string) => unknown;
  createScriptURL?: (input: string) => unknown;
};

type TrustedTypesFactoryLike = {
  createPolicy?: (
    policyName: string,
    policy: {
      createHTML?: (input: string) => string;
      createScript?: (input: string) => string;
      createScriptURL?: (input: string) => string;
    },
  ) => TrustedTypesPolicyLike;
  getPolicyNames?: () => string[];
  getPolicy?: (policyName: string) => TrustedTypesPolicyLike | null | undefined;
};

type TrustedTypesGlobalScope = typeof globalThis & {
  trustedTypes?: TrustedTypesFactoryLike;
  [REMOTE_LOADER_TRUSTED_TYPES_POLICY_GLOBAL_KEY]?: TrustedTypesPolicyLike | null;
};

let timerPatchDepth = 0;
let originalSetTimeout: typeof window.setTimeout | null = null;
let originalSetInterval: typeof window.setInterval | null = null;

const getTrustedTypesPolicy = () => {
  const scope = globalThis as TrustedTypesGlobalScope;
  const cachedPolicy = scope[REMOTE_LOADER_TRUSTED_TYPES_POLICY_GLOBAL_KEY];
  if (cachedPolicy) {
    return cachedPolicy;
  }

  const trustedTypesFactory = scope.trustedTypes;
  if (!trustedTypesFactory?.createPolicy) {
    return null;
  }

  try {
    const knownPolicyNames = trustedTypesFactory.getPolicyNames?.() ?? [];
    const policy = knownPolicyNames.includes(REMOTE_LOADER_TRUSTED_TYPES_POLICY)
      ? trustedTypesFactory.getPolicy?.(REMOTE_LOADER_TRUSTED_TYPES_POLICY) ?? null
      : trustedTypesFactory.createPolicy(REMOTE_LOADER_TRUSTED_TYPES_POLICY, {
          createHTML: (input) => input,
          createScript: (input) => input,
          createScriptURL: (input) => input,
        });

    scope[REMOTE_LOADER_TRUSTED_TYPES_POLICY_GLOBAL_KEY] = policy;
    return policy;
  } catch {
    return null;
  }
};

const createTrustedTimerHandler = (handler: TimerHandler) => {
  if (typeof handler !== "string") {
    return handler;
  }

  const policy = getTrustedTypesPolicy();
  return policy?.createScript ? policy.createScript(handler) : handler;
};

export const installTrustedTypesTimerCompat = () => {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (timerPatchDepth === 0) {
    originalSetTimeout = window.setTimeout;
    originalSetInterval = window.setInterval;

    window.setTimeout = ((handler: TimerHandler, timeout?: number, ...args: unknown[]) =>
      Reflect.apply(originalSetTimeout!, window, [
        createTrustedTimerHandler(handler),
        timeout,
        ...args,
      ]) as number) as typeof window.setTimeout;

    window.setInterval = ((handler: TimerHandler, timeout?: number, ...args: unknown[]) =>
      Reflect.apply(originalSetInterval!, window, [
        createTrustedTimerHandler(handler),
        timeout,
        ...args,
      ]) as number) as typeof window.setInterval;
  }

  timerPatchDepth += 1;

  return () => {
    timerPatchDepth = Math.max(0, timerPatchDepth - 1);

    if (timerPatchDepth === 0) {
      if (originalSetTimeout) {
        window.setTimeout = originalSetTimeout;
      }
      if (originalSetInterval) {
        window.setInterval = originalSetInterval;
      }

      originalSetTimeout = null;
      originalSetInterval = null;
    }
  };
};
